/**
 * Client-side mirror of the QuizGameSpec shape produced by the
 * generate-ai-game edge function. Kept as plain TS types (no runtime
 * validation here — the edge function validates before persisting).
 */

export type ArchetypeId =
  | "quiz-catcher"
  | "word-sort"
  | "matching-pairs"
  | "type-runner";

export type Difficulty = "easy" | "medium" | "hard";

export interface Question {
  prompt: string;
  correct: string;
  distractors?: string[];
  explanation?: string;
  bucket?: string;
}

export interface ThemePalette {
  bg: string;
  surface: string;
  accent: string;
  good: string;
  bad: string;
}

export interface QuizThemeSpec {
  title: string;
  palette: ThemePalette;
  buckets?: string[];
}

export interface DifficultyTuning {
  spawn_rate: number;
  fall_speed: number;
  timer_s: number;
  lives: number;
  question_time_s: number;
}

export interface QuizMeta {
  title: string;
  description: string;
  grade: string;
  difficulty: Difficulty;
  subject: string;
  topic?: string;
  estimated_minutes: number;
}

export interface QuizGameSpec {
  engine: "quiz-archetype";
  archetype: ArchetypeId;
  spec_version: number;
  engine_version: number;
  questions: Question[];
  theme: QuizThemeSpec;
  tuning: DifficultyTuning;
  meta: QuizMeta;
  fallback?: boolean;
  fallback_reason?: string;
  gameType?: "ai_dynamic";
}

export function isQuizGameSpec(cfg: unknown): cfg is QuizGameSpec {
  return (
    typeof cfg === "object" &&
    cfg !== null &&
    (cfg as any).engine === "quiz-archetype" &&
    Array.isArray((cfg as any).questions)
  );
}

export const DEFAULT_TUNING: Record<Difficulty, DifficultyTuning> = {
  easy: { spawn_rate: 1.1, fall_speed: 90, timer_s: 90, lives: 5, question_time_s: 20 },
  medium: { spawn_rate: 0.8, fall_speed: 130, timer_s: 75, lives: 3, question_time_s: 15 },
  hard: { spawn_rate: 0.55, fall_speed: 180, timer_s: 60, lives: 3, question_time_s: 10 },
};
